import { ButtonLink } from "@components/ui/Button/ButtonLink";
import { Divider } from "@components/ui/Divider";
import Icon from "@components/ui/Icon";
import { Close } from "@components/ui/Icon/icons/Close";
import { FACEBOOK_APP_ID } from "@constants";
import { City, Country } from "@graphql/types";
import { useNotification } from "@helpers/hooks/useNotification";
import {
  generateEmailShareLink,
  generateXTwitterShareLink,
} from "@helpers/share";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { FC, useEffect, useMemo, useState } from "react";

type IPostProps = {
  title: string;
  city?: Pick<City, "id" | "name" | "slug" | "url"> | null;
  country?: Pick<Country, "id" | "name" | "slug" | "url"> | null;
  url: string;
};

type IItemProps = {
  text: string;
  anchor: string;
};

type IProps = {
  post: IPostProps;
  items: IItemProps[];
};

export const BlogContentListDesktop = ({ items }: { items: IItemProps[] }) => {
  const [itemSelected, setItemSelected] = useState("");
  const [itemsHeigthToTop, setItemsHeigthToTop] = useState<number[]>([]);

  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const itemsHeight = items.map((item) => {
      const element = document.getElementById(item.anchor);
      return element?.offsetTop || 0;
    });
    setItemsHeigthToTop(itemsHeight);
  }, [router.asPath]);

  useEffect(() => {
    const handleScroll = () => {
      let lastSectionViewed = "";

      itemsHeigthToTop?.forEach((item, index) => {
        if (window.scrollY >= item) {
          lastSectionViewed = items[index].text;
        }
      });
      if (itemSelected !== lastSectionViewed) {
        setItemSelected(lastSectionViewed);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [itemsHeigthToTop]);

  return (
    <div className="sticky top-8 hei">
      <p className="font-bold text-base text-black">TABLE OF CONTENTS</p>
      <ul className="flex flex-col text-gray-600 mt-2 overflow-y-auto max-h-75-vh">
        {items.map((item, index) => (
          <li
            key={index}
            className={`mt-2 hover:text-black ${itemSelected === item.text ? "text-black" : "text-gray-500"} transition-all duration-300`}
          >
            <button
              className="text-left text-base font-medium "
              onClick={() => {
                document
                  .getElementById(item.anchor)
                  ?.scrollIntoView({ behavior: "smooth" });
                setItemSelected(item.text);
              }}
            >
              <p dangerouslySetInnerHTML={{ __html: item.text }} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const BlogContentListMobile = ({ items }: { items: IItemProps[] }) => {
  const [itemSelected, setItemSelected] = useState("");
  const [itemsHeigthToTop, setItemsHeigthToTop] = useState<number[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const itemsHeight = items.map((item) => {
      const element = document.getElementById(item.anchor);
      return element?.offsetTop || 0;
    });
    setItemsHeigthToTop(itemsHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      let lastSectionViewed = "";

      itemsHeigthToTop?.forEach((item, index) => {
        if (window.scrollY >= item) {
          lastSectionViewed = items[index].text;
        }
      });
      if (itemSelected !== lastSectionViewed) {
        setItemSelected(lastSectionViewed);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [itemsHeigthToTop]);

  return (
    <div className="text-center">
      <p className="font-bold text-black mb-4 text-lg">TABLE OF CONTENTS</p>
      <ul className="text-gray-600 max-h-50-vh overflow-y-auto flex flex-col">
        {items.map((item, index) => {
          return (
            <button
              key={index}
              className="text-left"
              onClick={() => {
                setTimeout(() => {
                  document
                    .getElementById(item.anchor)
                    ?.scrollIntoView({ behavior: "smooth" });
                  setItemSelected(item.text);
                }, 500);
              }}
            >
              <li
                key={index}
                className={`mt-2 text-left hover:text-black ${itemSelected === item.text ? "text-black" : "text-gray-400"}`}
              >
                <p dangerouslySetInnerHTML={{ __html: item.text }} />
              </li>
            </button>
          );
        })}
      </ul>
    </div>
  );
};

export const BlogShareMobile = ({ post }: { post: IPostProps }) => {
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setLinkCopied(false);
    }, 300);
  }, [linkCopied]);

  return (
    <div className="flex  flex-col items-center text-center">
      <p className="font-bold text-black mb-4 text-lg">SHARE</p>
      <ShareContainer post={post} />
    </div>
  );
};

const ShareContainer = ({ post }: { post: IPostProps }) => {
  const notification = useNotification();
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setLinkCopied(false);
    }, 300);
  }, [linkCopied]);

  const facebookLink = useMemo(() => {
    return `https://facebook.com/dialog/send?link=${post.url}&app_id=${FACEBOOK_APP_ID}&redirect_uri=https://facebook.com`;
  }, []);

  const xTwitterShareLink = useMemo(() => {
    return generateXTwitterShareLink(
      `Check out this article on @GoWithGuide: “${post.title}”\n#gowithguide\n${post.url}`
    );
  }, []);

  const emailLink = useMemo(() => {
    return generateEmailShareLink({
      subject: `Check out this article about ${post.country?.name} on GoWithGuide`,
      body: `I found this article about ${post.country?.name} on GoWithGuide and thought you'd love it: ${post.title} %0aHere is the link: ${post.url}`, //%0a used to break line
    });
  }, []);

  return (
    <ul className="flex">
      <li>
        <Link
          href={facebookLink}
          passHref
          target="_blank"
          title="gowithguide facebook share link"
          rel="noopener noreferrer"
        >
          <Icon icon="facebookRound" size="sm" />
        </Link>
      </li>
      <li className="ml-4">
        <Link
          href={xTwitterShareLink}
          passHref
          target="_blank"
          title="gowithguide x(twitter) share link"
          rel="noopener noreferrer"
        >
          <Icon icon="xtwitter" size="sm" />
        </Link>
      </li>
      <li className="ml-4">
        <a title="gowithguide email share link" href={emailLink}>
          <Icon icon="email" size="sm" className="text-gray-600" />
        </a>
      </li>
      <li className="ml-4">
        <button
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            setLinkCopied(true);
            notification.success("Link Copied");
          }}
        >
          <Icon
            icon="copy"
            size="sm"
            className={`${linkCopied ? "text-green-600" : "text-gray-600"}`}
          />
        </button>
      </li>
    </ul>
  );
};

const BlogSidebar: FC<IProps> = ({ post, items }) => {
  const [showBlogAd, setShowBlogAd] = useState(true);

  const redirectUrlBlogCta = post?.city?.name
    ? `/s?city=${post.city.slug}&t=guides`
    : post?.country?.url
      ? `${post.country.url}/guides`
      : "/destinations";

  const utmSeparator = redirectUrlBlogCta.includes("?") ? "&" : "?";
  const utmParams = `${utmSeparator}utm_medium=post&utm_source=blog-cta`;

  return (
    <>
      {/* Desktop: sibebar with share then TOC */}
      <div className="lg:w-1/4 ml-auto mt-8">
        <div className="hidden lg:flex flex-col sticky top-16 w-72 ml-auto px-8">
          <div title="share section" className="flex flex-row items-center">
            <p className="mr-4">Share</p>
            <ShareContainer post={post} />
          </div>
          <Divider className="my-6" />
          {items.length > 0 && <BlogContentListDesktop items={items} />}
        </div>
      </div>

      {/* for mobile screen, show in bottom with fixed */}
      {showBlogAd && (
        <div className="flex gap-2 lg:hidden fixed h-20 bg-blog_cta_v4_mobile_background text-black bottom-0 w-full z-20 py-4 px-3">
          <Close
            className="h-4 w-4 text-black absolute top-0 left-0 font-extrabold cursor-pointer"
            onClick={() => setShowBlogAd(false)}
          />
          <div className="flex items-center w-8/12">
            <p className="font-bold text-15px sm:text-base">
              Customize a private tour with the guide of your choice.
            </p>
          </div>
          <div className="flex flex-1 items-center w-4/12 justify-end">
            <ButtonLink
              size="xs"
              className="p-3 text-white sm:text-base"
              variant="blog-cta-v4"
              href={`${redirectUrlBlogCta}${utmParams}`}
              title="GoWithGuide-Find your guide"
            >
              Find your guide
            </ButtonLink>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogSidebar;
